// file created at 2020-1-20
// Auto-generated files D3RadialDendrogram.ts

import { Vue, Component, Watch } from 'vue-property-decorator';

import _ from 'lodash';
import * as d3 from 'd3';
import rawData from '@/data/19983881105_e93c2d8279_b.jpg.json';
import d3Data from '@/data/19983881105_e93c2d8279_b.jpg.tree.json';
import { preorder, postorder, getMu, setLeaves } from '@/utils/tree-utils';

interface Node {
  Ind: number;
  Type: string;
  Lind: number;
  Rind: number;
  NumLeaves: number;
  R: number;
  G: number;
  B: number;
  Ward: number;
  Sse: number;
  Var: number;
  Std: number;
  NumClusterSamples: number;
  x: number; // 노드의 x 좌표
  y: number; // 노드의 y 좌표;
  parentInd: number | null; // 부모 index
  depth: number; // root 노드로부터의 depth
  leftRightInfoList: Array<'left' | 'right'>; // root 노드로 부터 왼쪽 자식인지, 오른쪽 자식인지. ['left', 'right', 'left', 'left']
  depthRowIndex: number; // 일정 depth에서의 왼쪽시작 기준으로 해당 node의 index
  height: number; // Ward(or 다른 어떤 기준)의 합
  oneUnitHeight: number; // depth마다 1단위씩 올라가는 height
  isBalanced: boolean; // 자식들이 balanced 상태인지
  angle: number; // 중점을 기준으로 한 각도
  unitRow: number | null; // 부모노드와 자식노드의 가로 길이를 4단위로 만든 row좌표
}

interface NodesDict {
  [nodeId: number]: Node;
}

interface Edge {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

interface SubTreesDict {
  // 모두 담고 있다가, 현재 renderingNodes에 들어가 있는 애들만 빼올 수 있도록 한다.
  [root: number]: NodesDict;
}

interface NewNode {
  children: NewNode[];
  Leaves: Leave[];
  Samples: number[][];
  Centroid: number[];
  See: number;
  Ward: number;
  rgb: RgbColor;
}

interface Leave {
  Ind: number;
  From: number;
  Samples: number[][];
  Centroid: number[];
  Ward: number;
  Sse: number;
  rgb: RgbColor;
}

interface RgbColor {
  r: number;
  g: number;
  b: number;
}

@Component({})
export default class D3RadialMiddleCircle extends Vue {
  private svgWidth: number = 2000;
  private svgHeight: number = 2000;
  private cuttingCircleRadius: number = 380;
  private readonly heightConstant: number = 50;

  private cuttingHeight: number = 200;
  private heightStandard: string = '높이 일정';
  private heightStandardList: string[] = [
    '높이 일정',
    'Ward',
    'Sse',
    'Var',
    'Std'
  ];
  private depthStandard: number = 1;
  private allChildNodesShowed: boolean = false;
  private isUsedForResearch: boolean = true;

  private nodes: Node[] = [];
  private edges: Edge[] = [];
  private cuttingHeightLeafNodes: Node[] = [];
  private cuttingHeightLeafNodeChildren: Node[] = [];
  private renderingNodes: Node[] = []; // 실제 그리는 노드들
  private subTreesDict: SubTreesDict = {};

  private radialDendrogramSvg!: d3.Selection<
    SVGSVGElement,
    unknown,
    HTMLElement,
    any
  >;
  private dottedCircleG!: d3.Selection<
    SVGCircleElement,
    unknown,
    HTMLElement,
    any
  >;
  private nodesGId: string = 'nodes';
  private nodeLabelsGId: string = 'node-labels';
  private nodePlusesGId: string = 'node-pluses';
  private nodeRotationsGId: string = 'node-rotations';
  private nodeDepthsGId: string = 'node-depths';
  private nodeDepthTextsG!: d3.Selection<
    d3.BaseType,
    unknown,
    SVGGElement,
    unknown
  >;
  private edgesGId: string = 'edges';

  private data!: NewNode;

  private *makeTaehongData() {
    const d: any = d3Data;
    console.log('okay');
    postorder(d, setLeaves);
    preorder(d, n => (n.rgb = d3.lab.apply(null, n.Centroid).rgb()));
    preorder(d, n => console.log(getMu(n)));

    // return d;
    yield d;
  }

  private mounted() {
    const data: NewNode = this.makeTaehongData().next().value;
    console.log('data', data);
    this.data = data;
    console.log('hierarchy', d3.hierarchy(data));
    const radius: number = this.cuttingCircleRadius;
    const radialSize: [number, number] = [2 * Math.PI, radius];
    const layout = d3.tree<{}>()
      .size(radialSize)
      .separation((a, b) => (a.parent === b.parent ? 1 : 2) / a.depth);
    const root = layout(d3.hierarchy(data));
    const nodes = root.descendants().reverse();
    const links = root.links();
    console.log('nodes', nodes);
    console.log('links', links);

    this.radialDendrogramSvg = d3
      .select('#main-container')
      .append('svg')
      .attr('width', this.svgWidth)
      .attr('height', this.svgHeight);

    this.init();
  }

  private init() {
    this.radialDendrogramSvg.selectAll('g').remove();
    this.dottedCircleG = this.radialDendrogramSvg.append('g').append('circle');
    this.radialDendrogramSvg
      .append('g')
      .attr('id', this.edgesGId)
      .selectAll('line');
    this.radialDendrogramSvg
      .append('g')
      .attr('id', this.nodesGId)
      .selectAll('circle');
    this.radialDendrogramSvg
      .append('g')
      .attr('id', this.nodeLabelsGId)
      .selectAll('text');
    this.radialDendrogramSvg
      .append('g')
      .attr('id', this.nodePlusesGId)
      .selectAll('text');
    this.radialDendrogramSvg
      .append('g')
      .attr('id', this.nodeRotationsGId)
      .selectAll('text');
    this.radialDendrogramSvg
      .append('g')
      .attr('id', this.nodeDepthsGId)
      .selectAll('text');

    // nodes를 초기화한다.
    this.nodes = _.map(rawData, datum => {
      return {
        Ind: datum.Ind,
        Type: datum.Type,
        Lind: datum.Lind,
        Rind: datum.Rind,
        NumLeaves: datum.NumLeaves,
        R: datum.R,
        G: datum.G,
        B: datum.B,
        Ward: datum.Ward,
        Sse: datum.Sse,
        Var: datum.Var,
        Std: datum.Std,
        NumClusterSamples: datum.NumClusterSamples,
        x: 0,
        y: 0,
        parentInd: null,
        depth: -1,
        leftRightInfoList: [],
        depthRowIndex: -1,
        unitRow: null,
        height: 0,
        oneUnitHeight: 0,
        isBalanced: true,
        angle: 0
      };
    });
    this.edges = [];
    this.cuttingHeightLeafNodes = [];
    this.renderingNodes = [];

    const nodesDict: NodesDict = _.keyBy(this.nodes, node => node.Ind);

    // parent를 구한다.
    _.forEach(this.nodes, node => {
      if (nodesDict.hasOwnProperty(node.Lind)) {
        nodesDict[node.Lind].parentInd = node.Ind;
      }
      if (nodesDict.hasOwnProperty(node.Rind)) {
        nodesDict[node.Rind].parentInd = node.Ind;
      }
    });

    // root를 구한다.
    const rootNode: Node = _.filter(
      this.nodes,
      node => node.parentInd === null
    )[0];

    // 각 node마다 depth를 구한다
    _.forEach(this.nodes, node => {
      node.leftRightInfoList = this.makeNodeLeftRightInfoList({
        node,
        root: rootNode,
        nodesDict
      });
    });

    // 각 node마다 height를 구한다.
    _.forEach(this.nodes, node => {
      node.height = this.makeNodeHeight(node, nodesDict);
      node.oneUnitHeight = this.makeNodeOneUnitHeight(node, nodesDict);
    });

    // cuttingHeight의 leaf nodes를 구한다.
    this.cuttingHeightLeafNodes = this.makeCuttingHeightLeafNodes(
      nodesDict,
      this.cuttingHeight
    );
    // leaf nodes를 기준으로 subtree를 만든다.
    _.forEach(this.cuttingHeightLeafNodes, node => {
      this.subTreesDict[node.Ind] = {};
      this.subTreesDict[node.Ind][node.Ind] = node;

      // subtree에 모두 child들을 넣는다.
      const descendants = this.findAllDescendantNodes({
        parentNode: node,
        nodesDict
      });
      _.forEach(descendants, descendant => {
        this.subTreesDict[node.Ind][descendant.Ind] = descendant;
      });
    });

    // cuttingHeightLeafNodes를 원에 등각도로 배치한다.
    _.forEach(this.cuttingHeightLeafNodes, (node, index) => {
      node.angle = ((2 * Math.PI) / this.cuttingHeightLeafNodes.length) * index;
      node.x =
        this.svgWidth / 2 + this.cuttingCircleRadius * Math.cos(node.angle);

      node.y =
        this.svgHeight / 2 + this.cuttingCircleRadius * Math.sin(node.angle);
    });

    // 부모들을 찾는다
    this.renderingNodes = this.cuttingHeightLeafNodes;
    for (let i = 0; i < this.depthStandard; i++) {
      // 바로 위 부모들만 반환한다
      this.renderingNodes = this.makeCuttingParentNodesAndUnion(
        this.renderingNodes,
        nodesDict,
        this.heightStandard
      );
    }

    // 자식들을 찾는다
    if (this.allChildNodesShowed) {
      // TODO 함수형이 아님. this.renderingNodes가 변한다
      this.getAllAllChildNodes(nodesDict);
    }

    _.forEach(this.nodes, node => {
      // balanced인지 확인한다
      node.isBalanced = this.isBalanced(node, nodesDict);
      // depth를 만든다
      node.depth = this.makeNodeDepth({
        node,
        root: rootNode,
        nodesDict
      });
    });

    // renderingNodes에 대해 순서를 보장시킨다.
    this.renderingNodes = _.sortBy(this.renderingNodes, node => node.Ind);

    // edges를 만든다.
    this.edges = this.makeEdges(this.renderingNodes, nodesDict);

    const renderingNodesHash = _.keyBy(this.renderingNodes, node => node.Ind);
    _.forEach(this.renderingNodes, node => {
      if (node.Lind !== 0 && renderingNodesHash.hasOwnProperty(node.Lind)) {
        this.edges.push({
          x1: node.x as number,
          y1: node.y as number,
          x2: nodesDict[node.Lind].x as number,
          y2: nodesDict[node.Lind].y as number
        });
      }
      if (node.Rind !== 0 && renderingNodesHash.hasOwnProperty(node.Rind)) {
        this.edges.push({
          x1: node.x as number,
          y1: node.y as number,
          x2: nodesDict[node.Rind].x as number,
          y2: nodesDict[node.Rind].y as number
        });
      }
    });

    // d3로 그린다
    // 점선 원을 그린다
    const dottedCircle = {
      cx: this.svgWidth / 2,
      cy: this.svgHeight / 2,
      r: this.cuttingCircleRadius,
      fill: 'rgba(0,0,0,0)',
      stroke: 'black',
      strokeDasharray: '5 5'
    };
    this.dottedCircleG
      .attr('cx', dottedCircle.cx)
      .attr('cy', dottedCircle.cy)
      .attr('r', dottedCircle.r)
      .attr('fill', dottedCircle.fill)
      .attr('stroke', dottedCircle.stroke)
      .attr('stroke-dasharray', dottedCircle.strokeDasharray);

    // edge들을 그린다
    this.drawEdges({
      edges: this.edges,
      gId: this.edgesGId,
      svg: this.radialDendrogramSvg
    });
    // node들을 그린다
    this.drawNodes({
      renderingNodes: this.renderingNodes,
      gId: this.nodesGId,
      svg: this.radialDendrogramSvg
    });
    // node들에 대한 라벨을 그린다
    this.drawNodeLabels(this.renderingNodes, this.nodeLabelsGId);
    // node의 depth를 그린다
    this.drawNodeDepths({
      renderingNodes: this.renderingNodes,
      gId: this.nodeDepthsGId,
      svg: this.radialDendrogramSvg
    });
    // node의 확장 +를 그린다.
    this.drawNodePluses({
      renderingNodes: this.renderingNodes,
      nodesDict,
      gId: this.nodePlusesGId,
      svg: this.radialDendrogramSvg
    });
    // node의 rotation을 그린다
    this.drawNodeRotations({
      renderingNodes: this.renderingNodes,
      nodesDict,
      gId: this.nodeRotationsGId,
      svg: this.radialDendrogramSvg
    });
  }

  // 회전 이모티콘을 그리는 함수
  private drawNodeRotations(o: {
    renderingNodes: Node[];
    nodesDict: NodesDict;
    gId: string;
    svg: d3.Selection<SVGSVGElement, unknown, HTMLElement, any>;
  }) {
    const notBalancedNodes = _.filter(
      o.renderingNodes,
      node => !this.isBalanced(node, o.nodesDict)
    );

    const nodeRotations = o.svg
      .select(`#${o.gId}`)
      .selectAll('text')
      .data(notBalancedNodes);

    // enter
    nodeRotations
      .enter()
      .append('text')
      .text('ᘐ')
      .on('click', node => {
        this.rotateShell(node, o.nodesDict);

        const childNodes: Node[] = [];

        // cuttingleafNodes중 루트 노드를 찾는다.
        const subtreeRoot = this.findCuttingLeafRoot({
          node,
          subTreesDict: this.subTreesDict,
          nodesDict: o.nodesDict
        });
        console.log('subtreeRoot', subtreeRoot);

        // renderingNodes에 기존의 자손들을 찾는다.
        let preservedDescendants = _.filter(
          this.renderingNodes,
          renderingNode => {
            return (
              this.subTreesDict[subtreeRoot.Ind].hasOwnProperty(
                renderingNode.Ind
              ) && renderingNode.Ind !== subtreeRoot.Ind
            );
          }
        );

        let descendantNodes: Node[] = _.concat(
          childNodes,
          preservedDescendants
        );

        console.log('descendantNodes', descendantNodes);

        // descendantNodes에 대한 좌표정보를 받아온다.
        descendantNodes = this.makeChildNodesPosition(
          subtreeRoot,
          descendantNodes,
          o.nodesDict
        );

        // // 전에 있던 descendantNodes을 뺀다.
        // this.renderingNodes = _.differenceBy(
        //   this.renderingNodes,
        //   descendantNodes,
        //   childNode => childNode.Ind
        // );

        // descendantNodes 중 preservedDescendants를 찾는다
        preservedDescendants = descendantNodes;

        // // renderingNodes에 preservedDescendants를 넣는다.
        // this.renderingNodes = _.concat(
        //   this.renderingNodes,
        //   preservedDescendants
        // );

        // // renderingNodes에 대해 순서를 보장시킨다.
        // this.renderingNodes = _.sortBy(
        //   this.renderingNodes,
        //   renderingNode => renderingNode.Ind
        // );

        this.renderingNodes = _.concat(this.renderingNodes, childNodes);

        console.log('this.renderingNodes2', this.renderingNodes);

        this.edges = this.makeEdges(this.renderingNodes, o.nodesDict);

        // 노드 관련된 정보를 d3에서 다시 그린다.
        this.drawEdges({
          edges: this.edges,
          gId: this.edgesGId,
          svg: this.radialDendrogramSvg
        });
        this.drawNodes({
          renderingNodes: this.renderingNodes,
          gId: this.nodesGId,
          svg: this.radialDendrogramSvg
        });
        this.drawNodeLabels(this.renderingNodes, this.nodeLabelsGId);
        this.drawNodePluses({
          renderingNodes: o.renderingNodes,
          nodesDict: o.nodesDict,
          gId: o.gId,
          svg: o.svg
        });
        this.drawNodeRotations({
          renderingNodes: this.renderingNodes,
          nodesDict: o.nodesDict,
          gId: this.nodeRotationsGId,
          svg: this.radialDendrogramSvg
        });
      })
      .attr('x', node => node.x)
      .attr('y', node => node.y - 12)
      .attr('text-anchor', 'middle')
      .attr('fill', 'rgba(0,0,0,0)')
      .style('cursor', 'pointer')
      .transition()
      .duration(1000)
      .attr('fill', this.isUsedForResearch ? 'black' : 'none');

    // update
    nodeRotations
      .text('ᘐ')
      .attr('x', node => node.x)
      .attr('y', node => node.y - 12)
      .attr('fill', this.isUsedForResearch ? 'black' : 'none');

    // exit
    nodeRotations.exit().remove();
  }

  /**
   * edge들을 만들어 반환하는 함수
   * @param nodes
   */
  private makeEdges(renderingNodes: Node[], nodesDict: NodesDict): Edge[] {
    const edges: Edge[] = [];

    const renderingNodesHash = _.keyBy(renderingNodes, node => node.Ind);
    _.forEach(renderingNodes, node => {
      if (node.Lind !== 0 && renderingNodesHash.hasOwnProperty(node.Lind)) {
        edges.push({
          x1: node.x as number,
          y1: node.y as number,
          x2: nodesDict[node.Lind].x as number,
          y2: nodesDict[node.Lind].y as number
        });
      }
      if (node.Rind !== 0 && renderingNodesHash.hasOwnProperty(node.Rind)) {
        edges.push({
          x1: node.x as number,
          y1: node.y as number,
          x2: nodesDict[node.Rind].x as number,
          y2: nodesDict[node.Rind].y as number
        });
      }
    });

    return edges;
  }

  // node depth들을 그린다
  private drawNodeDepths(o: {
    renderingNodes: Node[];
    svg: d3.Selection<SVGSVGElement, unknown, HTMLElement, any>;
    gId: string;
  }) {
    const nodeDepths = o.svg
      .select(`#${o.gId}`)
      .selectAll('text')
      .data(o.renderingNodes);

    // enter
    nodeDepths
      .enter()
      .append('text')
      .text(node => `${node.depth}d`)
      .attr('x', node => node.x - 20)
      .attr('y', node => node.y + 4)
      .attr('text-anchor', 'middle')
      .attr('fill', 'rgba(0,0,0,0)')
      .transition()
      .duration(1000)
      .attr('fill', this.isUsedForResearch ? '#777777' : 'none');

    // update
    nodeDepths
      .text(node => `${node.depth}d`)
      .attr('x', node => node.x - 20)
      .attr('y', node => node.y + 4)
      .attr('text-anchor', 'middle')
      .attr('fill', 'rgba(0,0,0,0)')
      .transition()
      .duration(1000)
      .attr('fill', this.isUsedForResearch ? '#777777' : 'none');

    // exit
    nodeDepths.exit().remove();
  }

  // node가 자식이 있다면 '+'을 그린다
  private drawNodePluses(o: {
    renderingNodes: Node[];
    nodesDict: NodesDict;
    gId: string;
    svg: d3.Selection<SVGSVGElement, unknown, HTMLElement, any>;
  }) {
    // 자식이 보이면 플러스 버튼이 없다.
    const renderingNodesDict: NodesDict = _.keyBy(
      this.renderingNodes,
      node => node.Ind
    );
    // 자식이 아직 안보이는 nodes
    const havingChildNodes: Node[] = _.filter(
      this.renderingNodes,
      node =>
        // renderingNodes에 자식들이 안보이는 경우에 넣는다.
        (!renderingNodesDict.hasOwnProperty(node.Lind) && node.Lind !== 0) ||
        (!renderingNodesDict.hasOwnProperty(node.Rind) && node.Rind !== 0)
    );

    const nodePluses = o.svg
      .select(`#${o.gId}`)
      .selectAll('text')
      .data(havingChildNodes);

    // enter
    nodePluses
      .enter()
      .append('text')
      .text('+')
      .on('click', node => {
        console.log('this.renderingNodes1', this.renderingNodes);

        // 누르면 바로 아래 자식 2개가 펼쳐진다.
        let childNodes: Node[] = [
          o.nodesDict[node.Lind],
          o.nodesDict[node.Rind]
        ];

        // cuttingleafNodes중 루트 노드를 찾는다.
        const subtreeRoot = this.findCuttingLeafRoot({
          node,
          subTreesDict: this.subTreesDict,
          nodesDict: o.nodesDict
        });

        // renderingNodes에 기존의 자손들을 찾는다.
        let preservedDescendants = _.filter(
          this.renderingNodes,
          renderingNode => {
            return (
              this.subTreesDict[subtreeRoot.Ind].hasOwnProperty(
                renderingNode.Ind
              ) && renderingNode.Ind !== subtreeRoot.Ind
            );
          }
        );

        let descendantNodes: Node[] = _.concat(
          childNodes,
          preservedDescendants
        );

        // descendantNodes에 대한 좌표정보를 받아온다.
        descendantNodes = this.makeChildNodesPosition(
          subtreeRoot,
          descendantNodes,
          o.nodesDict
        );

        // // 전에 있던 descendantNodes을 뺀다.
        // this.renderingNodes = _.differenceBy(
        //   this.renderingNodes,
        //   descendantNodes,
        //   childNode => childNode.Ind
        // );

        // descendantNodes 중 childNodes를 찾는다
        childNodes = _.filter(descendantNodes, descendantNode => {
          return (
            descendantNode.Ind === childNodes[0].Ind ||
            descendantNode.Ind === childNodes[1].Ind
          );
        });

        // descendantNodes 중 preservedDescendants를 찾는다
        preservedDescendants = _.filter(descendantNodes, descendantNode => {
          return (
            descendantNode.Ind !== childNodes[0].Ind &&
            descendantNode.Ind !== childNodes[1].Ind
          );
        });

        // // renderingNodes에 preservedDescendants를 넣는다.
        // this.renderingNodes = _.concat(
        //   this.renderingNodes,
        //   preservedDescendants
        // );

        // // renderingNodes에 대해 순서를 보장시킨다.
        // this.renderingNodes = _.sortBy(
        //   this.renderingNodes,
        //   renderingNode => renderingNode.Ind
        // );

        this.renderingNodes = _.concat(this.renderingNodes, childNodes);

        console.log('this.renderingNodes2', this.renderingNodes);

        this.edges = this.makeEdges(this.renderingNodes, o.nodesDict);

        // 노드 관련된 정보를 d3에서 다시 그린다.
        this.drawEdges({
          edges: this.edges,
          gId: this.edgesGId,
          svg: this.radialDendrogramSvg
        });
        this.drawNodes({
          renderingNodes: this.renderingNodes,
          gId: this.nodesGId,
          svg: this.radialDendrogramSvg
        });
        this.drawNodeLabels(this.renderingNodes, this.nodeLabelsGId);
        this.drawNodePluses({
          renderingNodes: o.renderingNodes,
          nodesDict: o.nodesDict,
          gId: o.gId,
          svg: o.svg
        });
        this.drawNodeRotations({
          renderingNodes: this.renderingNodes,
          nodesDict: o.nodesDict,
          gId: this.nodeRotationsGId,
          svg: this.radialDendrogramSvg
        });
      })
      .attr('x', node => node.x + 16)
      .attr('y', node => node.y + 4)
      .attr('text-anchor', 'middle')
      .style('cursor', 'pointer')
      .attr('fill', 'rgba(0,0,0,0)')
      .transition()
      .duration(1000)
      .attr('fill', 'black');

    // update
    nodePluses.attr('x', node => node.x + 16).attr('y', node => node.y + 4);

    // exit
    nodePluses.exit().remove();
  }

  // edge들을 그린다
  private drawEdges(o: {
    edges: Edge[];
    gId: string;
    svg: d3.Selection<SVGSVGElement, unknown, HTMLElement, any>;
  }) {
    const edges = o.svg
      .select(`#${o.gId}`)
      .selectAll('line')
      .data(o.edges);

    // append
    edges
      .enter()
      .append('line')
      .attr('x1', edge => edge.x1)
      .attr('y1', edge => edge.y1)
      .attr('x2', edge => edge.x2)
      .attr('y2', edge => edge.y2)
      .style('stroke', 'rgba(0,0,0,0)')
      .transition()
      .duration(1000)
      .style('stroke', 'rgba(0,0,0,0.5)')
      .style('stroke-width', 1);

    // update
    edges
      .attr('x1', edge => edge.x1)
      .attr('y1', edge => edge.y1)
      .attr('x2', edge => edge.x2)
      .attr('y2', edge => edge.y2);

    // exit
    edges.exit().remove();
  }

  private drawNodeLabels(renderingNodes: Node[], gId: string) {
    const nodeLabels = this.radialDendrogramSvg
      .select(`#${gId}`)
      .selectAll('text')
      .data(renderingNodes);

    // enter
    nodeLabels
      .enter()
      .append('text')
      .text(node => node.Ind)
      .attr('x', node => node.x)
      .attr('y', node => node.y + 22)
      .attr('text-anchor', 'middle')
      .attr('fill', 'rgba(0,0,0,0)')
      .transition()
      .duration(1000)
      .attr('fill', this.isUsedForResearch ? 'black' : 'none');

    // update
    nodeLabels
      .text(node => node.Ind)
      .attr('fill', this.isUsedForResearch ? 'black' : 'none')
      .transition()
      .duration(1000)
      .attr('x', node => node.x)
      .attr('y', node => node.y + 22);

    // exit
    nodeLabels.exit().remove();
  }

  /**
   * node를 그린다
   * @param renderingNodes
   */
  private drawNodes(o: {
    renderingNodes: Node[];
    gId: string;
    svg: d3.Selection<SVGSVGElement, unknown, HTMLElement, any>;
  }) {
    const nodes = o.svg
      .select(`#${o.gId}`)
      .selectAll('circle')
      .data(o.renderingNodes);

    // enter
    nodes
      .enter()
      .append('circle')
      .attr('cx', node => node.x)
      .attr('cy', node => node.y)
      .attr('r', 10)
      .attr('fill', node => `rgba(0,0,0,0)`)
      .attr('stroke', node => 'rgba(0,0,0,0)')
      .attr('stroke-width', 3)
      .style('cursor', node => (node.isBalanced ? 'auto' : 'pointer'))
      .transition()
      .duration(1000)
      .attr('fill', node => `rgb(${node.R}, ${node.G}, ${node.B})`);
    // .attr('stroke', node => (node.isBalanced ? 'rgba(0,0,0,0)' : `magenta`));

    // update
    nodes
      .attr('fill', node => `rgb(${node.R}, ${node.G}, ${node.B})`)
      .transition()
      .duration(1000)
      .attr('cx', node => node.x)
      .attr('cy', node => node.y);

    // exit
    nodes.exit().remove();
  }

  // 어떤 node가 cuttingLeafNode 중의 root를 찾는 함수
  private findCuttingLeafRoot(o: {
    node: Node;
    subTreesDict: SubTreesDict;
    nodesDict: NodesDict;
  }): Node {
    if (o.subTreesDict.hasOwnProperty(o.node.Ind)) {
      return o.node;
    } else {
      return this.findCuttingLeafRoot({
        node: o.nodesDict[o.node.parentInd as number],
        subTreesDict: o.subTreesDict,
        nodesDict: o.nodesDict
      });
    }
  }

  /**
   * childNode들에 대한 좌표를 반환한다.
   * @param childNodes
   * @param nodesDict
   */
  private makeChildNodesPosition(
    root: Node,
    childNodes: Node[],
    nodesDict: NodesDict
  ): Node[] {
    // TODO 여기서 depth가 변하면 마무리 그릴 때 다시 depth를 계산해야 한다.
    root.depth = 0;
    root.unitRow = null;
    _.forEach(childNodes, childNode => {
      childNode.unitRow = null;
    });

    _.forEach(childNodes, childNode => {
      childNode.depth = this.makeNodeDepth({
        node: childNode,
        root,
        nodesDict
      });
      // leftRightInfo를 만든다
      childNode.leftRightInfoList = this.makeNodeLeftRightInfoList({
        node: childNode,
        root,
        nodesDict
      });
      // rowDepthIndex를 구한다
      childNode.depthRowIndex = this.makeNodeDepthRowIndex(
        childNode.leftRightInfoList
      );

      // node의 unitRow 좌표를 찾는다.
      nodesDict = this.makeNodeUnitRow({
        node: childNode,
        root,
        nodesDict
      });
    });

    let subTreeNodesDict: NodesDict = _.keyBy(
      childNodes,
      childNode => childNode.Ind
    );
    subTreeNodesDict[root.Ind] = root;
    subTreeNodesDict = this.makeNotOverlaped(subTreeNodesDict);

    // 좌표를 만든다.
    _.forEach(childNodes, childNode => {
      // root 기준으로 unitRow만큼 각도를 움직인다.
      // 점선원_중점 + (점선원_반지름 + constantHeight) * cos(node.unitRow)
      childNode.angle = root.angle - (childNode.unitRow as number) / 180;
      // TODO rotation 시 unitRow가 문제가 있다.
      // console.log(
      //   'root.angle',
      //   root.angle,
      //   'childNode.unitRow',
      //   childNode.unitRow
      // );
      const depth = this.makeNodeDepth({
        node: childNode,
        root,
        nodesDict
      });

      if (this.heightStandard !== '높이 일정') {
        const distanceBetweenNodeAndRoot: number = this.makeDistanceBetweenNodeAndRoot(
          {
            childNode,
            root,
            nodesDict,
            heightStandard: this.heightStandard
          }
        );
        childNode.x =
          this.svgWidth / 2 +
          (this.cuttingCircleRadius + distanceBetweenNodeAndRoot) *
            Math.cos(childNode.angle);
        childNode.y =
          this.svgHeight / 2 +
          (this.cuttingCircleRadius + distanceBetweenNodeAndRoot) *
            Math.sin(childNode.angle);
      } else {
        childNode.x =
          this.svgWidth / 2 +
          (this.cuttingCircleRadius + this.heightConstant * depth) *
            Math.cos(childNode.angle);
        childNode.y =
          this.svgHeight / 2 +
          (this.cuttingCircleRadius + this.heightConstant * depth) *
            Math.sin(childNode.angle);
      }
    });

    return childNodes;
  }

  /**
   * node부터 rootNode까지의 거리를 반환하는 함수
   * @param childNode
   */
  private makeDistanceBetweenNodeAndRoot(o: {
    childNode: Node;
    root: Node;
    nodesDict: NodesDict;
    heightStandard: string;
  }): number {
    let distance: number = 0;
    let currentNode = o.childNode;
    while (
      currentNode.parentInd !== null &&
      currentNode.parentInd !== o.root.parentInd
    ) {
      const parentNode = o.nodesDict[currentNode.parentInd];
      // distance += parentNode[o.heightStandard];
      // distance += Math.sqrt(parentNode[o.heightStandard] + 2500);
      distance += 20 * Math.log2(parentNode[o.heightStandard] + 1);
      currentNode = parentNode;
    }
    return distance;
  }

  /**
   * **임시함수. 모든 cuttingNodes들의 child노드들을 갖온다.
   */
  private getAllAllChildNodes(nodesDict: NodesDict) {
    _.forEach(this.cuttingHeightLeafNodes, node => {
      const childNodes = this.findAllDescendantNodes({
        parentNode: node,
        nodesDict
      });

      node.depth = 0;
      _.forEach(childNodes, childNode => {
        childNode.depth = this.makeNodeDepth({
          node: childNode,
          root: node,
          nodesDict
        });
        // leftRightInfo를 만든다
        childNode.leftRightInfoList = this.makeNodeLeftRightInfoList({
          node: childNode,
          root: node,
          nodesDict
        });
        // rowDepthIndex를 구한다
        childNode.depthRowIndex = this.makeNodeDepthRowIndex(
          childNode.leftRightInfoList
        );

        // node의 unitRow 좌표를 찾는다.
        nodesDict = this.makeNodeUnitRow({
          node: childNode,
          root: node,
          nodesDict
        });
      });

      let subTreeNodesDict: NodesDict = _.keyBy(
        childNodes,
        childNode => childNode.Ind
      );
      subTreeNodesDict[node.Ind] = node;
      subTreeNodesDict = this.makeNotOverlaped(subTreeNodesDict);

      // 좌표를 만든다.
      _.forEach(childNodes, childNode => {
        // root 기준으로 unitRow만큼 각도를 움직인다.
        // 점선원_중점 + (점선원_반지름 + constantHeight) * cos(node.unitRow)
        childNode.angle = node.angle - (childNode.unitRow as number) / 180;
        const depth = this.makeNodeDepth({
          node: childNode,
          root: node,
          nodesDict
        });

        if (this.heightStandard !== '높이 일정') {
          const distanceBetweenNodeAndRoot: number = this.makeDistanceBetweenNodeAndRoot(
            {
              childNode,
              root: node,
              nodesDict,
              heightStandard: this.heightStandard
            }
          );
          childNode.x =
            this.svgWidth / 2 +
            (this.cuttingCircleRadius + distanceBetweenNodeAndRoot) *
              Math.cos(childNode.angle);
          childNode.y =
            this.svgHeight / 2 +
            (this.cuttingCircleRadius + distanceBetweenNodeAndRoot) *
              Math.sin(childNode.angle);
        } else {
          childNode.x =
            this.svgWidth / 2 +
            (this.cuttingCircleRadius + this.heightConstant * depth) *
              Math.cos(childNode.angle);
          childNode.y =
            this.svgHeight / 2 +
            (this.cuttingCircleRadius + this.heightConstant * depth) *
              Math.sin(childNode.angle);
        }
      });

      this.renderingNodes = _.concat(this.renderingNodes, childNodes);
    });
  }

  /**
   * 모든 자손 노드들을 찾아 반환하는 함수
   * @param parentNode
   * @param nodesDict
   */
  private findAllDescendantNodes(o: {
    parentNode: Node;
    nodesDict: NodesDict;
  }): Node[] {
    let childNodes: Node[] = [];
    if (o.parentNode.Lind !== 0) {
      const leftChild: Node = o.nodesDict[o.parentNode.Lind];
      childNodes.push(leftChild);
      const grandChildNodes: Node[] = this.findAllDescendantNodes({
        parentNode: leftChild,
        nodesDict: o.nodesDict
      });
      childNodes = _.concat(childNodes, grandChildNodes);
    }
    if (o.parentNode.Rind !== 0) {
      const rightChild: Node = o.nodesDict[o.parentNode.Rind];
      childNodes.push(rightChild);
      const grandChildNodes: Node[] = this.findAllDescendantNodes({
        parentNode: rightChild,
        nodesDict: o.nodesDict
      });
      childNodes = _.concat(childNodes, grandChildNodes);
    }

    return childNodes;
  }

  /**
   * 해당 node와 그 부모들의 node에 unitX 좌표를 형성한 nodesDict를 반환하는 함수이다.
   */
  private makeNodeUnitRow(o: {
    node: Node;
    root: Node;
    nodesDict: NodesDict;
  }): NodesDict {
    if (o.node.parentInd !== null && o.node.Ind !== o.root.Ind) {
      // 부모 node에 대한 unitX를 먼저 구한다
      let parentNode = o.nodesDict[o.node.parentInd];
      if (parentNode.unitRow === null) {
        o.nodesDict = this.makeNodeUnitRow({
          node: parentNode,
          root: o.root,
          nodesDict: o.nodesDict
        });
      }
      parentNode = o.nodesDict[o.node.parentInd];

      // parent의 왼쪽자식인지 오른쪽자식인지 알아야 한다.
      if (o.node.Ind === parentNode.Lind) {
        o.node.unitRow = (parentNode.unitRow as number) - 4;
      } else if (o.node.Ind === parentNode.Rind) {
        o.node.unitRow = (parentNode.unitRow as number) + 4;
      }

      o.nodesDict[o.node.Ind] = o.node;
    } else {
      o.node.unitRow = 0;
    }

    console.log('o.node.unitRow', o.node.unitRow);

    return o.nodesDict;
  }

  /**
   * leftRightInfoList로 depthRowIndex를 구하는 함수
   * @param node
   */
  private makeNodeDepthRowIndex(
    leftRightInfoList: Array<'left' | 'right'>
  ): number {
    let depthRowIndex: number = 0;
    _.forEach(leftRightInfoList, (info, index) => {
      if (index < leftRightInfoList.length - 1) {
        if (info === 'right') {
          depthRowIndex += Math.pow(2, leftRightInfoList.length - 1 - index);
        }
      } else {
        if (info === 'right') {
          depthRowIndex += 1;
        }
      }
    });

    return depthRowIndex;
  }

  /**
   * 부모 노드들을 찾고 자식노드드과 부모노드들을 union해서 반환하는 함수
   * @param currentNodes
   * @param nodesDict
   * @param heightStandard
   */
  private makeCuttingParentNodesAndUnion(
    currentNodes: Node[],
    nodesDict: NodesDict,
    heightStandard: string
  ): Node[] {
    // 바로 위 부모만 해본다
    const parentNodesDict: { [nodeIndex: number]: Node } = {};
    const cuttingHeightLeafNodesDict = _.keyBy(currentNodes, node => node.Ind);

    _.forEach(currentNodes, node => {
      if (node.parentInd !== null) {
        const parentNode = nodesDict[node.parentInd];

        if (
          cuttingHeightLeafNodesDict.hasOwnProperty(parentNode.Lind) &&
          cuttingHeightLeafNodesDict.hasOwnProperty(parentNode.Rind)
        ) {
          parentNodesDict[node.parentInd] = nodesDict[node.parentInd];
        }
      }
    });

    _.forEach(parentNodesDict, node => {
      // 자식들의 중점을 구한다.
      // 현재 node의 Ward가 필요하다
      // 각도가 필요하다
      let leftChildNode!: Node;
      let rightChildNode!: Node;

      if (cuttingHeightLeafNodesDict.hasOwnProperty(node.Lind)) {
        leftChildNode = cuttingHeightLeafNodesDict[node.Lind];
      }

      if (cuttingHeightLeafNodesDict.hasOwnProperty(node.Rind)) {
        rightChildNode = cuttingHeightLeafNodesDict[node.Rind];
      }

      if (!_.isNil(leftChildNode) && !_.isNil(rightChildNode)) {
        const dx: number = rightChildNode.x - leftChildNode.x;
        const dy: number = rightChildNode.y - leftChildNode.y;
        const centerX: number =
          leftChildNode.x <= rightChildNode.x
            ? leftChildNode.x + Math.abs(dx) / 2
            : rightChildNode.x + Math.abs(dx) / 2;
        const centerY: number =
          leftChildNode.y <= rightChildNode.y
            ? leftChildNode.y + Math.abs(dy) / 2
            : rightChildNode.y + Math.abs(dy) / 2;

        const dxdyAngle: number = this.getAngle({
          startX: leftChildNode.x,
          startY: leftChildNode.y,
          endX: rightChildNode.x,
          endY: rightChildNode.y
        });

        const centerAngle: number = dxdyAngle + Math.PI / 2;
        if (heightStandard !== '높이 일정') {
          // node.x = centerX + (node[heightStandard]) * Math.cos(centerAngle);
          // node.y = centerY + (node[heightStandard]) * Math.sin(centerAngle);
          // node.x =
          //   centerX +
          //   Math.sqrt(node[heightStandard] + 2500) * Math.cos(centerAngle);
          // node.y =
          //   centerY +
          //   Math.sqrt(node[heightStandard] + 2500) * Math.sin(centerAngle);
          node.x =
            centerX +
            20 * Math.log2(node[heightStandard] + 1) * Math.cos(centerAngle);
          node.y =
            centerY +
            20 * Math.log2(node[heightStandard] + 1) * Math.sin(centerAngle);
        } else {
          node.x = centerX + this.heightConstant * Math.cos(centerAngle);
          node.y = centerY + this.heightConstant * Math.sin(centerAngle);
        }
      }
    });

    const parentNodes = _.values(parentNodesDict);
    const unionedNodes = _.chain(currentNodes)
      .concat(parentNodes)
      .unionBy(node => node.Ind)
      .value();

    return unionedNodes;
  }

  /**
   * 각도를 구하는 함수
   */
  private getAngle(o: {
    startX: number;
    startY: number;
    endX: number;
    endY: number;
  }): number {
    const dy: number = o.endY - o.startY;
    const dx: number = o.endX - o.startX;
    const angle: number = Math.atan2(dy, dx);

    return angle;
  }

  /**
   * 노드의 depth와 루트 기준으로 왼쪽 오른쪽자식 계보를 나타내주는 leftRightInfoList (ex. ['left', 'left', 'right', ...])
   * @param node
   * @param nodesDict
   */
  private makeNodeDepthAndLeftRightInfoList(
    node: Node,
    nodesDict: NodesDict
  ): Node {
    let currentNode: Node = node;
    let depth: number = 0;
    const leftRightInfoList: Array<'left' | 'right'> = [];

    while (currentNode.parentInd !== null) {
      depth += 1;
      const parentNode: Node = nodesDict[currentNode.parentInd];

      if (currentNode.Ind === parentNode.Lind) {
        leftRightInfoList.unshift('left');
      } else if (currentNode.Ind === parentNode.Rind) {
        leftRightInfoList.unshift('right');
      }

      currentNode = parentNode;
    }

    node.depth = depth;
    node.leftRightInfoList = leftRightInfoList;

    return node;
  }

  private makeNodeDepth(o: {
    node: Node;
    root: Node;
    nodesDict: NodesDict;
  }): number {
    let currentNode: Node = o.node;
    let depth: number = 0;

    while (currentNode.parentInd !== null && currentNode.Ind !== o.root.Ind) {
      depth += 1;
      const parentNode: Node = o.nodesDict[currentNode.parentInd];

      currentNode = parentNode;
    }

    return depth;
  }

  private makeNodeLeftRightInfoList(o: {
    node: Node;
    root: Node;
    nodesDict: NodesDict;
  }): Array<'left' | 'right'> {
    let currentNode: Node = o.node;
    const leftRightInfoList: Array<'left' | 'right'> = [];

    while (currentNode.parentInd !== null && currentNode.Ind !== o.root.Ind) {
      const parentNode: Node = o.nodesDict[currentNode.parentInd];

      if (currentNode.Ind === parentNode.Lind) {
        leftRightInfoList.unshift('left');
      } else if (currentNode.Ind === parentNode.Rind) {
        leftRightInfoList.unshift('right');
      }

      currentNode = parentNode;
    }

    return leftRightInfoList;
  }

  /**
   * cuttingHeight 기준으로 잘려진 후의 leaf node들을 구하는 함수
   * @param nodesDict
   * @param cuttingHeight
   */
  private makeCuttingHeightLeafNodes(
    nodesDict: NodesDict,
    cuttingHeight: number
  ): Node[] {
    const cuttingHeightLeafNodes: Node[] = [];
    _.forEach(nodesDict, node => {
      if (
        node.parentInd !== null &&
        cuttingHeight >= node.height &&
        cuttingHeight < nodesDict[node.parentInd].height
      ) {
        cuttingHeightLeafNodes.push(node);
      }
      if (
        node.parentInd !== null &&
        cuttingHeight <= node.height &&
        node.Type === 'leaf'
      ) {
        cuttingHeightLeafNodes.push(node);
      }
    });

    // 같은 부모가 있는 child노드들에 대한 순서 보장을 위한 sorting.
    // 일단은 되는 듯하다.
    cuttingHeightLeafNodes.sort((node1, node2) => {
      let result: number = 0;

      const standardLeftRightInfoList: Array<'left' | 'right'> =
        node1.leftRightInfoList.length <= node2.leftRightInfoList.length
          ? node1.leftRightInfoList
          : node2.leftRightInfoList;

      for (let i = 0; i < standardLeftRightInfoList.length; i++) {
        if (
          node1.leftRightInfoList[i] === 'left' &&
          node2.leftRightInfoList[i] === 'right'
        ) {
          result = -1;
          break;
        } else if (
          node1.leftRightInfoList[i] === 'right' &&
          node2.leftRightInfoList[i] === 'left'
        ) {
          result = 1;
          break;
        }
      }

      return result;
    });

    return cuttingHeightLeafNodes;
  }

  /**
   * node의 height를 구하는 함수
   * @param node
   * @param nodesDict
   */
  private makeNodeHeight(node: Node, nodesDict: NodesDict): number {
    if (!node) {
      return 0;
    }
    if (node.Lind === 0 && node.Rind === 0) {
      return 0;
    }
    return (
      node.Ward +
      Math.max(
        this.makeNodeHeight(nodesDict[node.Lind], nodesDict),
        this.makeNodeHeight(nodesDict[node.Rind], nodesDict)
      )
    );
  }

  // 해당 노드의 child 노드들이 balanced인지 확인하는 함수
  private isBalanced(node: Node, nodesDict: NodesDict): boolean {
    if (!node) {
      return true;
    }
    return (
      1 >= this.getChildrenDepthDifference(node, nodesDict) &&
      this.isBalanced(nodesDict[node.Lind], nodesDict) &&
      this.isBalanced(nodesDict[node.Rind], nodesDict)
    );
  }

  // 왼쪽자식, 오른쪽 자식의 높이차를 반환하는 함수
  private getChildrenDepthDifference(node: Node, nodesDict: NodesDict) {
    if (!node || (node.Lind === 0 && node.Rind === 0)) {
      return 0;
    }
    return Math.abs(
      nodesDict[node.Lind].oneUnitHeight - nodesDict[node.Rind].oneUnitHeight
    );
  }

  // 실제 변화가 있으려면 이 후에 다시 rowDepth와 unitX를 조정해야 하지 않나?
  // 노드를 기준으로 tree-rotation 하는 함수
  // 오른쪽 자식이 높다면, 회전방향을 왼쪽으로 잡는다
  private rotateShell(node: Node, nodesDict: NodesDict) {
    const leftChild = nodesDict[node.Lind];
    const rightChild = nodesDict[node.Rind];
    // 더 높은 height를 찾았는데,
    // index가 왼쪽인지 오른쪽인지 판단하네.
    // console.log('leftChild.oneUnitHeight', leftChild.oneUnitHeight);
    // console.log('rightChild.oneUnitHeight', rightChild.oneUnitHeight);
    const rotationDir: 'Lind' | 'Rind' =
      leftChild.oneUnitHeight >= rightChild.oneUnitHeight ? 'Rind' : 'Lind';
    // left: 0, right: 1

    this.rotate(
      node,
      rotationDir,
      nodesDict[node.parentInd as number],
      nodesDict
    );
  }
  // 자신과 회전 방향, 부모를 가지고
  private rotate(
    root: Node,
    RS: 'Lind' | 'Rind',
    parent: Node,
    nodesDict: NodesDict
  ) {
    console.log(
      'We are rotating the root',
      root.Ind,
      'to the',
      RS === 'Rind' ? 'right' : 'left'
    );

    const pivot: Node = this.internal(root, RS, nodesDict);
    this.replaceChild(parent, root, pivot);
    return pivot;
  }
  // pivot을 구하는 함수
  private internal(root: Node, RS: 'Lind' | 'Rind', nodesDict: NodesDict) {
    const OS: string = RS === 'Lind' ? 'Rind' : 'Lind';

    console.log('The opposite direction is', OS === 'Rind' ? 'right' : 'left');

    const pivot: Node = nodesDict[root[OS]];

    console.log(
      'The pivot is',
      pivot.Ind,
      'which is',
      OS === 'Rind' ? 'right' : 'left',
      'child of',
      root.Ind
    );

    root[OS] = pivot[RS];
    nodesDict[pivot[RS]].parentInd = root.Ind;

    console.log(
      'The',
      OS === 'Rind' ? 'right' : 'left',
      'child of',
      root.Ind,
      'is now',
      pivot[RS],
      ', which is',
      RS === 'Rind' ? 'right' : 'left',
      'child of the pivot',
      pivot.Ind
    );

    pivot[RS] = root.Ind;
    root.parentInd = pivot.Ind;

    console.log(
      'The',
      RS === 'Rind' ? 'right' : 'left',
      'child of',
      pivot.Ind,
      'is now',
      root.Ind,
      ', which is the root'
    );

    return pivot;
  }
  // of의 왼쪽 자식 오른쪽 자식 중 from과 같다면, to로 해당 자식을 변경하는 함수
  private replaceChild(of: Node, from: Node, to: Node) {
    if (of.Lind === from.Ind) {
      console.log(
        'The left child of',
        of.Ind,
        'is changed from',
        from.Ind,
        ',which is the root, to',
        to.Ind,
        ',which is the pivot'
      );
      of.Lind = to.Ind;
      to.parentInd = of.Ind;
    } else if (of.Rind === from.Ind) {
      console.log(
        'The right child of',
        of.Ind,
        'is changed from',
        from.Ind,
        ',which is the root, to',
        to.Ind,
        ',which is the pivot'
      );
      of.Rind = to.Ind;
      to.parentInd = of.Ind;
    } else {
      // tslint:disable-next-line: quotemark
      throw new Error("There's no such child.");
    }
  }

  /**
   * node의 depth마다 1단위씩 증가하는 height를 구하는 함수
   * @param node
   * @param nodesDict
   */
  private makeNodeOneUnitHeight(node: Node, nodesDict: NodesDict): number {
    if (!node) {
      return 0;
    }
    if (node.Lind === 0 && node.Rind === 0) {
      return 0;
    }
    return (
      1 +
      Math.max(
        this.makeNodeOneUnitHeight(nodesDict[node.Lind], nodesDict),
        this.makeNodeOneUnitHeight(nodesDict[node.Rind], nodesDict)
      )
    );
  }

  /**
   * node들이 겹치지 않게 만드는 함수
   * @param nodesDict 한정적인 nodesDict
   */
  private makeNotOverlaped(nodesDict: NodesDict): NodesDict {
    let testIndex: number = 0;
    while (testIndex < 10000) {
      const dislocatedPositionNodes = this.findDislocatedPositionNodes(
        nodesDict
      );

      if (dislocatedPositionNodes.length !== 0) {
        const sameParentNode = this.findSameAncestorNode(
          dislocatedPositionNodes[0],
          dislocatedPositionNodes[1],
          nodesDict
        );

        // sameParentNode를 기준으로 왼쪽 오른쪽 이동시킨다.
        nodesDict = this.moveSelfAndDescendants(
          nodesDict[sameParentNode.Rind],
          nodesDict,
          4
        );
        nodesDict = this.moveSelfAndDescendants(
          nodesDict[sameParentNode.Lind],
          nodesDict,
          -4
        );
      } else {
        console.log('break');
        break;
      }
      testIndex++;
    }

    testIndex = 0;
    while (testIndex < 10000) {
      const smallGapPositionNodes: Node[] = this.findSmallGapPositionNodes(
        nodesDict,
        8
      );

      if (smallGapPositionNodes.length !== 0) {
        const sameParentNode = this.findSameAncestorNode(
          smallGapPositionNodes[0],
          smallGapPositionNodes[1],
          nodesDict
        );

        nodesDict = this.moveSelfAndDescendants(
          nodesDict[sameParentNode.Rind],
          nodesDict,
          1
        );
        nodesDict = this.moveSelfAndDescendants(
          nodesDict[sameParentNode.Lind],
          nodesDict,
          -1
        );
      } else {
        console.log('break2');
        break;
      }

      testIndex++;
    }

    return nodesDict;
  }

  private findSmallGapPositionNodes(nodesDict: NodesDict, gap: number): Node[] {
    const smallGapPositionNodes: Node[] = [];

    const keys: string[] = Object.keys(nodesDict);

    for (let i = 0; i < keys.length - 1; i++) {
      for (let j = i + 1; j < keys.length; j++) {
        const node1: Node = nodesDict[keys[i]];
        const node2: Node = nodesDict[keys[j]];
        if (node1.depth === node2.depth) {
          if (
            (node1.depthRowIndex < node2.depthRowIndex &&
              (node2.unitRow as number) - (node1.unitRow as number) < gap) ||
            (node1.depthRowIndex > node2.depthRowIndex &&
              (node1.unitRow as number) - (node2.unitRow as number) < gap)
          ) {
            smallGapPositionNodes.push(node1);
            smallGapPositionNodes.push(node2);
          }
        }
      }
    }

    return smallGapPositionNodes;
  }

  /**
   * 자신과 자손들을 오른쪽으로 이동하는 함수
   * @param node
   * @param nodesDict
   */
  private moveSelfAndDescendants(
    node: Node,
    nodesDict: NodesDict, // 한정적인 nodesDict
    variance: number
  ): NodesDict {
    (node.unitRow as number) += variance;

    // 자식들이 null이 아닌 경우에는 계속 탐색한다.
    if (nodesDict.hasOwnProperty(node.Rind)) {
      // console.log(
      //   'nodesDictVariable[nodeVaiable.Rind]',
      //   nodesDictVariable[nodeVaiable.Rind]
      // );
      nodesDict = this.moveSelfAndDescendants(
        nodesDict[node.Rind],
        nodesDict,
        variance
      );
    }
    if (nodesDict.hasOwnProperty(node.Lind)) {
      // console.log(
      //   'nodesDictVariable[nodeVaiable.Lind]',
      //   nodesDictVariable[nodeVaiable.Lind]
      // );
      nodesDict = this.moveSelfAndDescendants(
        nodesDict[node.Lind],
        nodesDict,
        variance
      );
    }

    return nodesDict;
  }

  /**
   * 두 노드의 가장 가까운 조상 노드를 찾는다.
   * @param node1
   * @param node2
   */
  private findSameAncestorNode(node1: Node, node2: Node, nodesDict: NodesDict) {
    let sameAncestorNode!: Node;
    let currentNode1: Node = node1;
    let currentNode2: Node = node2;
    while (true) {
      if (currentNode1.parentInd === currentNode2.parentInd) {
        if (currentNode1.parentInd === null) {
          sameAncestorNode = currentNode1;
        } else {
          sameAncestorNode = nodesDict[currentNode1.parentInd];
        }
        break;
      } else {
        // node들이 다른경우 다시 탐색한다.
        currentNode1 = nodesDict[currentNode1.parentInd as number];
        currentNode2 = nodesDict[currentNode2.parentInd as number];
      }
    }

    return sameAncestorNode;
  }

  /**
   * 같은 depth의 node들 간에 위치가 안맞는 경우 node를 반환하는 함수
   * @param nodesDict
   */
  private findDislocatedPositionNodes(nodesDict: NodesDict) {
    const dislocatedPositionNodes: Node[] = [];

    const keys: string[] = Object.keys(nodesDict);

    for (let i = 0; i < keys.length - 1; i++) {
      for (let j = i + 1; j < keys.length; j++) {
        const node1: Node = nodesDict[keys[i]];
        const node2: Node = nodesDict[keys[j]];

        if (node1.depth === node2.depth) {
          if (
            (node1.depthRowIndex < node2.depthRowIndex &&
              (node1.unitRow as number) >= (node2.unitRow as number)) ||
            (node1.depthRowIndex > node2.depthRowIndex &&
              (node1.unitRow as number) <= (node2.unitRow as number))
          ) {
            dislocatedPositionNodes.push(node1);
            dislocatedPositionNodes.push(node2);
          }
        }
      }
    }

    return dislocatedPositionNodes;
  }

  @Watch('isUsedForResearch')
  private isUsedForResearchChanged(isUsedForResearch: boolean) {
    const nodesDict = _.keyBy(this.nodes, node => node.Ind);
    // edge들을 그린다
    this.drawEdges({
      edges: this.edges,
      gId: this.edgesGId,
      svg: this.radialDendrogramSvg
    });
    // node들을 그린다
    this.drawNodes({
      renderingNodes: this.renderingNodes,
      gId: this.nodesGId,
      svg: this.radialDendrogramSvg
    });
    // node들에 대한 라벨을 그린다
    this.drawNodeLabels(this.renderingNodes, this.nodeLabelsGId);
    // node의 depth를 그린다
    this.drawNodeDepths({
      renderingNodes: this.renderingNodes,
      gId: this.nodeDepthsGId,
      svg: this.radialDendrogramSvg
    });
    // node의 확장 +를 그린다.
    this.drawNodePluses({
      renderingNodes: this.renderingNodes,
      nodesDict,
      gId: this.nodePlusesGId,
      svg: this.radialDendrogramSvg
    });
    // node의 rotation을 그린다
    this.drawNodeRotations({
      renderingNodes: this.renderingNodes,
      nodesDict,
      gId: this.nodeRotationsGId,
      svg: this.radialDendrogramSvg
    });
  }

  @Watch('allChildNodesShowed')
  private allChildNodesShowedChanged(allChildNodesShowed: boolean) {
    this.init();
  }
}
